import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import history from '../utils/history';
import { checkIfHotjarEnable, timeBetweenDates } from '../utils/helpers';
import * as sinisterCardsActions from '../redux/actions/sinister-cards';
import { bankList, accountList } from '../data/bankList';
import RimacTopBar from '../components/common/RimacTopBar/RimacTopBar';
import Navbar from '../components/common/Navbar/Navbar';
import InfoCard from '../components/content/InfoCard/InfoCard';
import Select from '../components/common/select/Select';
import Input from '../components/common/input/Input';
import ImgRadio from '../components/common/ImgRadio/ImgRadio';
import Modal from '../components/common/modal/Modal';
import InfoIcon from '../assets/infoIcon.svg';
import MarkIcon from '../assets/ic_tooltip.svg';
import ModalImg from '../assets/ModalImage.svg';

const checkValidAccountNumber = (
  currencyType,
  value,
  currentBank,
  accountType
) => {
  if (!/^\d+$/.test(value)) return true;

  let hasError = false;
  switch (currentBank) {
    case 'INT':
      if (value.length !== 13) hasError = true;
      break;
    case 'BCP':
      if (currencyType === 'dollars') {
        if (
          accountType === 'Ahorro' &&
          (value.length !== 14 || value.slice(11, 12) !== '1')
        )
          hasError = true;
        if (
          accountType === 'Corriente' &&
          (value.length !== 13 || value.slice(10, 11) !== '1')
        )
          hasError = true;
      } else if (currencyType === 'soles') {
        if (
          accountType === 'Ahorro' &&
          (value.length !== 14 || value.slice(11, 12) !== '0')
        )
          hasError = true;
        if (
          accountType === 'Corriente' &&
          (value.length !== 13 || value.slice(10, 11) !== '0')
        )
          hasError = true;
      }
      break;
    case 'SCT':
      if (value.length !== 10) hasError = true;
      if (
        accountType &&
        accountType === 'Corriente' &&
        value.slice(0, 3) !== '000'
      ) {
        hasError = true;
      }
      break;
    case 'BBVA':
      if (value.length !== 20 || value.slice(0, 3) !== '011') hasError = true;

      // if(accountType) {
      //   if(accountType === 'Corriente' && value.slice(10, 12) !== '01') hasError = true;
      //   if(accountType === 'Ahorro' && value.slice(10, 12) !== '02') hasError = true;
      // }

      break;
    default:
      break;
  }

  return hasError;
};

const warningMsgByBank = (currentBank, accountType) => {
  let msg = '';

  switch (currentBank) {
    case 'INT':
      msg = 'Debe tener 13 dígitos';
      break;
    case 'BCP':
      if (accountType === 'Ahorro') {
        msg = 'Debe tener 14 dígitos';
      } else {
        msg = 'Debe tener 13 dígitos';
      }
      break;
    case 'SCT':
      msg = 'Debe tener 10 dígitos';
      break;
    case 'BBVA':
      msg = 'Debe tener 20 dígitos';
      break;
    default:
      break;
  }

  return msg;
};

const RefundInfoPage = () => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const soles = useSelector((state) => state.sinisterCards.totalSoles);
  const dollars = useSelector((state) => state.sinisterCards.totalDollars);
  const accountInfo = useSelector((state) => state.sinisterCards.accountInfo);
  const [bank, setBank] = useState({
    bankSoles:
      accountInfo.length > 0 && accountInfo[0].bank
        ? accountInfo[0].bank.value
        : '',
    bankDollars:
      accountInfo.length > 0 && accountInfo[1].bank
        ? accountInfo[1].bank.value
        : '',
  });
  const [accountType, setAccountType] = useState({
    accountTypeSoles:
      accountInfo.length > 0 ? accountInfo[0].accountType : null,
    accountTypeDollars:
      accountInfo.length > 0 ? accountInfo[1].accountType : null,
  });
  const [accountNumber, setAccountNumber] = useState({
    accountNumberSoles: accountInfo.length > 0 ? accountInfo[0].cci : '',
    accountNumberDollars: accountInfo.length > 0 ? accountInfo[1].cci : '',
  });
  const [accountError, setAccountError] = useState({
    errorSoles: false,
    errorDollars: false,
  });
  const [viewStartTime, setViewStartTime] = useState(new Date());

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    return () => {
      const endTime = new Date();
      const userTotalTime = timeBetweenDates(viewStartTime, endTime);

      if (checkIfHotjarEnable()) {
        window.digitalData.push({
          task: {
            name: 'Tiempo registrado - Info reembolso',
            time: userTotalTime,
          },
          event: 'trackTimeToComplete',
        });
      }
    };
  }, []);

  const isNextActive = () => {
    let isSolesValid = true;
    let isDollarsValid = true;

    if (Number(soles) > 0) {
      isSolesValid =
        bank.bankSoles !== '' &&
        accountType.accountTypeSoles &&
        accountNumber.accountNumberSoles !== '' &&
        !accountError.errorSoles;
    }

    if (Number(dollars) > 0) {
      isDollarsValid =
        bank.bankDollars !== '' &&
        accountType.accountTypeDollars &&
        accountNumber.accountNumberDollars !== '' &&
        !accountError.errorDollars;
    }

    return isSolesValid && isDollarsValid;
  };

  const setBankSoles = (val) => {
    if (checkIfHotjarEnable() && bank.bankSoles) {
      window.digitalData.push({
        action: {
          group: 'Tramites Siniestros – Protección Tarjeta BBVA',
          category: 'Banco reembolso',
          name: bank.bankSoles,
          label: 'Editar',
        },
        event: 'trackAction',
      });
    }

    setBank({
      ...bank,
      bankSoles: val,
    });

    setAccountType({
      ...accountType,
      accountTypeSoles: null,
    });

    setAccountNumber({
      ...accountNumber,
      accountNumberSoles: '',
    });

    setAccountError({
      ...accountError,
      errorSoles: false,
    });
  };

  const setBankDollars = (val) => {
    if (checkIfHotjarEnable() && bank.bankDollars) {
      window.digitalData.push({
        action: {
          group: 'Tramites Siniestros – Protección Tarjeta BBVA',
          category: 'Banco reembolso',
          name: bank.bankDollars,
          label: 'Editar',
        },
        event: 'trackAction',
      });
    }

    setBank({
      ...bank,
      bankDollars: val,
    });

    setAccountType({
      ...accountType,
      accountTypeDollars: null,
    });

    setAccountNumber({
      ...accountNumber,
      accountNumberDollars: '',
    });

    setAccountError({
      ...accountError,
      errorDollars: false,
    });
  };

  const setAccountTypeSoles = (val) => {
    setAccountType({
      ...accountType,
      accountTypeSoles: val,
    });

    setAccountNumber({
      ...accountNumber,
      accountNumberSoles: '',
    });
  };

  const setAccountTypeDollars = (val) => {
    setAccountType({
      ...accountType,
      accountTypeDollars: val,
    });

    setAccountNumber({
      ...accountNumber,
      accountNumberDollars: '',
    });
  };

  const setAccountNumberSoles = (val) => {
    let otherError = false;
    const hasAccountNumberError = checkValidAccountNumber(
      'soles',
      val,
      bank.bankSoles,
      accountType.accountTypeSoles.value
    );

    if (
      bank.bankSoles === bank.bankDollars &&
      accountType.accountTypeSoles === accountType.accountTypeDollars &&
      val === accountNumber.accountNumberDollars
    ) {
      otherError = true;
    }

    if (hasAccountNumberError || otherError) {
      setAccountError({
        ...accountError,
        errorSoles: true,
      });
    } else {
      setAccountError({
        ...accountError,
        errorSoles: false,
      });
    }

    setAccountNumber({
      ...accountNumber,
      accountNumberSoles: val,
    });
  };

  const setAccountNumberDollars = (val) => {
    let otherError = false;
    const hasAccountNumberError = checkValidAccountNumber(
      'dollars',
      val,
      bank.bankDollars,
      accountType.accountTypeDollars.value
    );

    if (
      bank.bankSoles === bank.bankDollars &&
      accountType.accountTypeSoles === accountType.accountTypeDollars &&
      val === accountNumber.accountNumberSoles
    ) {
      otherError = true;
    }

    if (hasAccountNumberError || otherError) {
      setAccountError({
        ...accountError,
        errorDollars: true,
      });
    } else {
      setAccountError({
        ...accountError,
        errorDollars: false,
      });
    }

    setAccountNumber({
      ...accountNumber,
      accountNumberDollars: val,
    });
  };

  const onBackHandler = () => {
    dispatch(sinisterCardsActions.reduceStep());
    history.replace('/montos-robados');
  };

  const onContinueHandler = () => {
    const solesExist = Number(soles) > 0;
    const dollarsExist = Number(dollars) > 0;
    const accountInfoArray = [
      {
        currency: 'PEN',
        bank: solesExist
          ? bankList.find((currBank) => currBank.value === bank.bankSoles)
          : null,
        accountType: solesExist ? accountType.accountTypeSoles : null,
        cci: solesExist ? accountNumber.accountNumberSoles : '',
      },
      {
        currency: 'DOL',
        bank: dollarsExist
          ? bankList.find((currBank) => currBank.value === bank.bankDollars)
          : null,
        accountType: dollarsExist ? accountType.accountTypeDollars : null,
        cci: dollarsExist ? accountNumber.accountNumberDollars : '',
      },
    ];

    if (checkIfHotjarEnable()) {
      if (solesExist) {
        window.digitalData.push({
          metadata: {
            key: 'Información Bancaria – Protección Tarjeta BBVA',
            value: `${bank.bankSoles} - ${accountType.accountTypeSoles.value}`,
          },
          event: 'trackMetadata',
        });
      }

      if (dollarsExist) {
        window.digitalData.push({
          metadata: {
            key: 'Información Bancaria – Protección Tarjeta BBVA',
            value: `${bank.bankDollars} - ${accountType.accountTypeDollars.value}`,
          },
          event: 'trackMetadata',
        });
      }
    }

    dispatch(sinisterCardsActions.setAccountInfo(accountInfoArray));
    dispatch(sinisterCardsActions.incrementStep());
    history.push('/resumen');
  };

  return (
    <>
      <RimacTopBar />
      <div
        data-testid="refund-info-page"
        className="refundInfoPage pageContainer"
      >
        <div className="refundInfoPage__left pageContainer__left">
          <div className="titleCont">
            <h1 className="u-mb-24">Seguro de Protección de Tarjetas</h1>
            <p>
              Hola, sabemos que sufriste un siniestro y queremos ayudarte a
              reportarlo.
            </p>
          </div>
        </div>
        <div className="refundInfoPage__right pageContainer__right">
          <div className="titleCont">
            <h3 className="u-mb-16">¿En qué banco desea su reembolso?</h3>
          </div>

          <InfoCard
            icon={InfoIcon}
            customClasses={'u-mt-16 u-pad-16'}
            content={
              <p>
                <b>Recuerda:</b> Utilizaremos tus datos bancarios, sólo si el
                siniestro es <b>aprobado</b>.
              </p>
            }
          />
          {Number(soles) > 0 && (
            <div>
              {!bank.bankSoles && (
                <>
                  <h4 className="u-mt-40">Selecciona un banco</h4>
                  <p className="u-mt-16">Selecciona una de las opciones</p>
                </>
              )}
              <fieldset className="bankSelection u-mt-24 u-mb-40">
                {bankList.map((currBank, i) => {
                  if (currBank.type === 'initial') {
                    return (
                      <ImgRadio
                        key={i}
                        title={currBank.text}
                        value={currBank.value}
                        icon={currBank.logo}
                        name="bankSoles"
                        onItemClick={setBankSoles}
                        hide={bank.bankSoles ? true : false}
                        selected={
                          currBank.value === bank.bankSoles ? true : false
                        }
                        onEditClick={setBankSoles}
                      />
                    );
                  }

                  return null;
                })}
              </fieldset>

              {bank.bankSoles && (
                <div className="bankInfo">
                  <h4 className="u-mb-16">Completa la siguiente información</h4>
                  <p className="u-mb-24">
                    Recuerda que la cuenta debe ser del titular del seguro
                  </p>
                  <div className="bankInfo__splitInput">
                    <div
                      className="bankInfo__inputCont"
                      id="selectAccountSoles"
                    >
                      <Select
                        items={accountList}
                        selected={accountType.accountTypeSoles}
                        onClick={setAccountTypeSoles}
                        label="Tipo de cuenta"
                      />
                    </div>
                    <div className="bankInfo__inputCont" id="inputCoinSoles">
                      <Input
                        type="text"
                        label="Moneda"
                        value="Soles (S/)"
                        disabled
                      />
                    </div>
                  </div>
                  {accountType.accountTypeSoles ? (
                    <div className="bankInfo__inputCont" id="inputCCISoles">
                      <Input
                        type="text"
                        label={
                          bank.bankSoles === 'BBVA'
                            ? 'CCI - Cuenta Interbancaria'
                            : 'Número cuenta bancaria'
                        }
                        value={accountNumber.accountNumberSoles}
                        onChange={setAccountNumberSoles}
                        maxLength={20}
                        hasError={
                          accountError.errorSoles
                            ? '*Ingresa el formato correcto'
                            : ''
                        }
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="questionBar u-mb-24">
            {bank.bankSoles && accountType.accountTypeSoles ? (
              <>
                <p className="warningText">
                  {warningMsgByBank(
                    bank.bankSoles,
                    accountType.accountTypeSoles?.value
                  )}
                </p>
                <div
                  className="questionBar__cont"
                  onClick={() => setModalOpen(true)}
                >
                  <img src={MarkIcon} alt="icono pregunta" />
                  <p>¿Tienes alguna duda?</p>
                </div>
              </>
            ) : (
              <></>
            )}
          </div>

          {Number(dollars) > 0 && (
            <div>
              {!bank.bankDollars && (
                <>
                  <h4 className="u-mt-40">Selecciona un banco</h4>
                  <p className="u-mt-16">Selecciona una de las opciones</p>
                </>
              )}
              <fieldset className="bankSelection u-mt-24 u-mb-40">
                {bankList.map((currBank, i) => {
                  if (currBank.type === 'initial') {
                    return (
                      <ImgRadio
                        key={i}
                        itemKey={i}
                        title={currBank.text}
                        value={currBank.value}
                        name="bankDollars"
                        onItemClick={setBankDollars}
                        icon={currBank.logo}
                        hide={bank.bankDollars ? true : false}
                        selected={
                          currBank.value === bank.bankDollars ? true : false
                        }
                        onEditClick={setBankDollars}
                      />
                    );
                  }

                  return null;
                })}
              </fieldset>

              {bank.bankDollars && (
                <div className="bankInfo">
                  <h4 className="u-mb-16">Completa la siguiente información</h4>
                  <p className="u-mb-24">
                    Recuerda que la cuenta debe ser del titular del seguro
                  </p>
                  <div className="bankInfo__splitInput">
                    <div
                      className="bankInfo__inputCont"
                      id="selectAccountSoles"
                    >
                      <Select
                        items={accountList}
                        selected={accountType.accountTypeDollars}
                        onClick={setAccountTypeDollars}
                        label="Tipo de cuenta"
                      />
                    </div>
                    <div className="bankInfo__inputCont" id="inputCoinSoles">
                      <Input
                        type="text"
                        label="Moneda"
                        value="Dolares (US$)"
                        disabled
                      />
                    </div>
                  </div>
                  {accountType.accountTypeDollars ? (
                    <div className="bankInfo__inputCont" id="inputCCIDollars">
                      <Input
                        type="text"
                        label={
                          bank.bankDollars === 'BBVA'
                            ? 'CCI - Cuenta Interbancaria'
                            : 'Número cuenta bancaria'
                        }
                        value={accountNumber.accountNumberDollars}
                        onChange={setAccountNumberDollars}
                        maxLength={20}
                        hasError={
                          accountError.errorDollars
                            ? '*Ingresa el formato correcto'
                            : ''
                        }
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="questionBar">
            {bank.bankDollars && accountType.accountTypeDollars ? (
              <>
                <p className="warningText">
                  {warningMsgByBank(
                    bank.bankDollars,
                    accountType.accountTypeDollars?.value
                  )}
                </p>
                <div
                  className="questionBar__cont"
                  onClick={() => setModalOpen(true)}
                >
                  <img src={MarkIcon} alt="icono pregunta" />
                  <p>¿Tienes alguna duda?</p>
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>

        <div data-testid="sinister-detail-modal" className="sinisterModal">
          <Modal
            onLabelClosed={true}
            visible={modalOpen}
            onToggle={() => setModalOpen(false)}
            className={modalOpen ? 'sinisterModalMain' : ''}
            onLabelTextClosed=""
          >
            <div className="sinisterModalMain__info">
              <div className="sinisterModalMain__info-container">
                <h1>¿Tienes alguna duda sobre el rembolso?</h1>
                <div className="sinisterModalMain__info-content">
                  <div>
                    <ul>
                      <li>
                        Si tu cuenta se BBVA, por favor ingresa el código de
                        cuenta interbancario (CCI).
                      </li>
                      <li>
                        Si tu cuenta es Interbank, Scotiabank o BCP, por favor
                        ingresar el número de cuenta.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="sinisterModalMain__info-shadow"></div>
            </div>
          </Modal>
        </div>

        <Navbar
          navActive
          isNextDisabled={!isNextActive()}
          currentStep={4}
          totalSteps={5}
          onContinueClick={onContinueHandler}
          onBackClick={onBackHandler}
        />

        <div className="spacingLeft"></div>
      </div>
    </>
  );
};

export default RefundInfoPage;
